import React from "react";
import { Iconography, IconographySizes } from "@hid-galaxy-ui/galaxy-react";
import { useNavigate } from "react-router";
import "./QuickNavigation.scss";
import { useAppSelector } from "../../../redux";

type QuickNavigationVariant = "sidebar" | "flyout";

type QuickNavigationProps = {
  variant?: QuickNavigationVariant;
};

const quickNavigationCards = {
  "Portals & Environments": [
    {
      link: "https://cert.portal.origo.hidglobal.com/mobile-identities/#/home",
      name: "Origo Management Portal - CERT",
      icon: "globe",
    },
    {
      link: "https://portal.origo.hidglobal.com/mobile-identities/#/home",
      name: "Origo Management Portal - Prod",
      icon: "globe",
    },
    {
      link: "https://preprod.portal.origo.hidglobal.com/mobile-identities/#/home",
      name: "Origo Management Portal - Pre-Prod",
      icon: "globe",
    },
    {
      link: "https://developers.hidglobal.com/",
      name: "HID Developer Portal",
      icon: "globe",
    },
    {
      link: "/resources/feature-comparison-table",
      name: "Feature Comparison Table",
      icon: "globe",
    },
  ],
  "Service Status & Health": [
    {
      link: "https://status.cert.origo.hidglobal.com/",
      name: "Origo Service Status - CERT",
      icon: "globe",
    },
    {
      link: "https://status.origo.hidglobal.com/",
      name: "Origo Service Status - Prod",
      icon: "globe",
    },
  ],
};

const QuickNavigation: React.FC<QuickNavigationProps> = ({
  variant = "sidebar",
}) => {
  const navigate = useNavigate();
  const partnerDetails = useAppSelector(
    (state) => state.partner_details.partnerDetails
  );
  const [isFlyoutOpen, setIsFlyoutOpen] = React.useState(false);

  React.useEffect(() => {
    if (variant !== "flyout") return;

    if (isFlyoutOpen) {
      document.body.classList.add("quick-navigation-flyout-open");
    } else {
      document.body.classList.remove("quick-navigation-flyout-open");
    }

    return () => {
      document.body.classList.remove("quick-navigation-flyout-open");
    };
  }, [isFlyoutOpen, variant]);

  const handleQuickNavigationClick = (link: string) => {
    if (link.startsWith("http") || link.startsWith("/")) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/partner/${partnerDetails?.ID}/poc-dashboard/${link}`);
    }
  };

  const navigationContent = (
    <aside className="quick-navigation-list" aria-label="Quick navigation">
      {Object.entries(quickNavigationCards).map(([category, items]) => (
        <div key={category} className="quick-navigation-category">
          <h4 className="quick-navigation-category-heading">{category}</h4>
          <div className="quick-navigation-category-card">
            {items.map((item) => (
              <div
                key={item.link}
                className="quick-navigation-item"
                tabIndex={0}
                aria-label={`Open ${item.name}`}
                onClick={() => handleQuickNavigationClick(item.link)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleQuickNavigationClick(item.link);
                  }
                }}
              >
                <span
                  className="quick-navigation-icon"
                  style={{ color: "#003087" }}
                >
                  <Iconography
                    icon={item.icon}
                    className="icon"
                    size={IconographySizes.Medium}
                  />
                </span>
                <span className="quick-navigation-title">{item.name}</span>
                <Iconography
                  icon="arrowUpRight"
                  className="quick-navigation-arrow"
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </aside>
  );

  if (variant === "flyout") {
    return (
      <div className="quick-navigation-flyout">
        <button
          type="button"
          className={`quick-navigation-flyout__tab${
            isFlyoutOpen ? " quick-navigation-flyout__tab--open" : ""
          }`}
          aria-expanded={isFlyoutOpen}
          aria-controls="quick-navigation-flyout-panel"
          aria-label={isFlyoutOpen ? "Close Quick Actions" : "Open Quick Actions"}
          onClick={() => setIsFlyoutOpen((open) => !open)}
        >
          <span className="quick-navigation-flyout__tab-label">Quick Actions</span>
          <Iconography
            icon={isFlyoutOpen ? "chevronRight" : "chevronLeft"}
            size={IconographySizes.Small}
          />
        </button>

        {isFlyoutOpen ? (
          <button
            type="button"
            className="quick-navigation-flyout__backdrop"
            aria-label="Close Quick Actions"
            onClick={() => setIsFlyoutOpen(false)}
          />
        ) : null}

        <div
          id="quick-navigation-flyout-panel"
          className={`quick-navigation-flyout__panel${
            isFlyoutOpen ? " quick-navigation-flyout__panel--open" : ""
          }`}
          aria-hidden={!isFlyoutOpen}
        >
          <div className="quick-navigation-flyout__header">
            <div className="quick-navigation-flyout__header-title">
              <h3 className="quick-navigation-flyout__heading">Quick Actions</h3>
            </div>
            <button
              type="button"
              className="quick-navigation-flyout__close"
              aria-label="Close Quick Actions"
              onClick={() => setIsFlyoutOpen(false)}
            >
              <Iconography icon="xmark" size={IconographySizes.Medium} />
            </button>
          </div>
          {navigationContent}
        </div>
      </div>
    );
  }

  return (
    <div className="quick-navigation-container">
      <h3 className="quick-navigation-heading" style={{ margin: "20px 0px" }}>
        Quick Actions
      </h3>
      {navigationContent}
    </div>
  );
};

export default QuickNavigation;
